import api from "./api"
import { showErroPage, sortByAttribute } from "../utils/helper"
import { AxiosError } from "axios"
import { UpdateCompanyEmailRequest } from "../Interfaces"

interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  role: string
}

class UserService {

  private baseUrl = "/user";


  public createAdmin = async (userInfo: any, imageFile: File | null = null) => {
    if (!userInfo) throw new Error("No user info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}/admin`,
        { userInfo }
      );
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }
  public createOwner = async (userInfo: any, imageFile: File | null = null) => {
    if (!userInfo) throw new Error("No user info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}/owner`,
        { userInfo },
      );
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public createCompanyManager = async (userInfo: any, imageFile: File | null = null) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/company-manager`,
        { userInfo },
      );
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public createAreaManager = async (userInfo: any, organizationIds: number[], imageFile: File | null = null) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/area-manager`,
        { userInfo, organizationIds },
      );
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        throw Error(error.response?.statusText)
      } else {
        console.error(error);

      }
    }
  }

  public createManager = async (userInfo: any, locationIds: number[], imageFile: File | null = null) => {
    console.log("createManager called with userInfo:", userInfo)
    console.log("createManager called with locationIds:", locationIds)

    try {
      const response = await api.post(
        `${this.baseUrl}/manager`,
        { userInfo, locationIds },
      );
      console.log("createManager response:", response.data)
      // wait for image to upload before returning
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      console.error("createManager error:", error)
      if (error instanceof AxiosError) {
        console.error("createManager Axios error response:", error.response?.data)
        console.error("createManager Axios error status:", error.response?.status)
        throw Error(error.response?.statusText)
      } else {
        console.error("createManager non-Axios error:", error);
      }
    }
  }

  public createAssistantManager = async (userInfo: any, locationIds: number[], imageFile: File | null = null) => {
    try {

      const response = await api.post(
        `${this.baseUrl}/assistant-manager`,
        { userInfo, locationIds },
      );
      // wait for image to upload before returning
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public uploadProfileImage = async (imageFile: File, userId: number, firstName: string, lastName: string) => {
    if (!imageFile) return;
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/update/profile-image/${userId}/${firstName}-${lastName}`,
      )
      if (imageFile.type !== "image/jpeg" && imageFile.type !== "image/png" && imageFile.type !== "image/jpg") throw new Error("Invalid file type")
      const response = await api.put(
        signedUrl,
        imageFile,
        {
          headers: {
            "Content-Type": imageFile.type,
            "Authorization": undefined,
          }
        }
      );
      return response.data;
    } catch (error) {
      return error as Error;
    }
  };

  public getViewableProfileImage = async (userId: number, firstName: string, lastName: string) => {
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/profile-image/${userId}/${firstName}-${lastName}`, {
        timeout: 5000
      }
      )
      return signedUrl;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getOne = async (userId: number) => {
    if (!userId || userId === 0) {
      throw new Error("Invalid user ID")
    }

    console.log("Fetching user with ID:", userId)

    try {
      const response = await api.get(
        `${this.baseUrl}/${userId}`
      );

      console.log("User data received:", response.data)

      if (!response.data) {
        throw new Error("No user data received")
      }

      return response.data;
    } catch (error) {
      console.error("Error in getOne for userId:", userId, error)
      if (error instanceof AxiosError) {
        console.error("Axios error in getOne:", error.response);
        throw new Error(error.response?.statusText || "Failed to fetch user data")
      } else {
        console.error("Non-axios error in getOne:", error);
        throw error instanceof Error ? error : new Error("An unknown error occurred")
      }
    }
  }

  public getAllByUser = async (userId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-user/${userId}`
      )
      return { users: response.data }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error("Error in getAllByUser:", error.response?.data || error.message);
        throw new Error(error.response?.data?.message || error.message || "Failed to fetch users")
      } else {
        console.error("Non-axios error in getAllByUser:", error);
        throw new Error("An unexpected error occurred while fetching users")
      }
    }
  }
  public getSomeByUser = async (userId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-user/${userId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public getAllByLocation = async (locationId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-location/${locationId}`
      )
      sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }
  public getSomeByLocation = async (locationId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-location/${locationId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      // sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public getAllByOrganization = async (organizationId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-organization/${organizationId}`
      )
      sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }
  public getSomeByOrganization = async (organizationId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-organization/${organizationId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public updateTableOrder = async (tableId: number, tableName: string, displayOrder: number, id?: number) => {
    try {
      if (!id) {
        const response = await api.post(`${this.baseUrl}/create/table-order`, {
          tableName: tableName,
          tableId: tableId,
          displayOrder: displayOrder
        });
        return response.data
      }
      const response = await api.put(`${this.baseUrl}/update/table-order/${id}`, {
        tableName: tableName,
        tableId: tableId,
        displayOrder: displayOrder
      })
      return response.data
    } catch (error) {
      console.error("Failed to update order preferences:", error);
    }
  };

  public update = async (userInfo: any, organizationIds: number[] = [], locationIds: number[] = [], imageFile: File | null,) => {
    try {
      const response = await api.put(
        `${this.baseUrl}/update/user/${userInfo.id}`,
        // updatedUserInfo,
        { userInfo, organizationIds, locationIds },
      )
      if (imageFile) await this.uploadProfileImage(imageFile, response.data.id, response.data.firstName, response.data.lastName)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public updatePassword = async (userId: number, newPassword: string) => {
    try {
      const response = await api.put(
        `${this.baseUrl}/update/user/${userId}`,
        {
          userInfo: {
            id: userId,
            password: newPassword
          },
          organizationIds: [],
          locationIds: []
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public updateCompanyEmail = async (userId: number, companyEmail: boolean) => {
    try {
      const requestData: UpdateCompanyEmailRequest = {
        userId: userId,
        company_email: companyEmail
      };

      const response = await api.put(
        `${this.baseUrl}/update/company-email`,
        requestData
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public delete = async (id: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public addUsersToLocation = async (userIds: number[], locationId: number) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/add-to-location`,
        { userIds, locationId }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public removeUserFromLocation = async (userId: number, locationId: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/remove-from-location/${userId}/${locationId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public addUsersToOrganization = async (userIds: number[], organizationId: number) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/add-to-organization`,
        { userIds, organizationId }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public removeUserFromOrganization = async (userId: number, organizationId: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/remove-from-organization/${userId}/${organizationId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  async login(email: string, password: string): Promise<boolean> {
    try {
      const response = await api.post('/auth/login', { email, password })
      if (response.data.token) {
        localStorage.setItem('token', response.data.token)
        return true
      }
      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem('token')
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  }

  async checkPasswordResetRequired(userId: number): Promise<boolean> {
    try {
      const response = await api.get(`/users/${userId}/password-reset-required`)
      return response.data.requiresReset
    } catch (error) {
      console.error('Failed to check password reset status:', error)
      return false
    }
  }
}

export const userService = new UserService()